import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Text, Icon, Divider } from '@oneaudi/unified-web-components';
import {
  Headline as HeadLineAlpha,
  Text as TextAlpha,
  Icon as IconAlpha,
} from '@oneaudi/unified-web-alpha-components';
import { useSelector } from 'react-redux';
import {
  AprOrPayment,
  BonusOffer,
  DownPayment,
  MainOffer,
  OffersWrapper,
  PaymentLabel,
  Term,
  PaymentNumber,
} from './VehicleInfo.styles';
import type { OfferDetails } from '../../types';
import OffersTag from '../../assets/icons/OfferTagIcon';
import { numberFormatter } from '../../functions/priceFormat';
import {
  PercentData,
  Percent,
  PaymentData,
  TermCa,
  MainOfferData,
  OfferWrapper,
  DividerWrapper,
} from './Offers.styles';
import { selectAppConfig } from '../../redux/appConfigReducer';

interface OffersProps {
  offers: OfferDetails;
  offerType: string;
}

const Offers: React.FC<OffersProps> = ({ offers, offerType }) => {
  const { t, i18n } = useTranslation();

  const { market } = useSelector(selectAppConfig);
  const isUSMarket = market?.toLowerCase().includes('us');

  const isFinanceOffer = offerType === 'finance';

  const monthlyPayment = Number(offers.monthlyPayment?.replace(/[$,]/g, ''));

  const lang = i18n.language;

  const { aprRate, term, customerDownPayment, paymentFrequency, creditBonus, type, cpoTerm } =
    offers;

  const paymentFreq = useMemo(() => {
    if (paymentFrequency === 'WEEKLY') return t('digitalLeads.vehicle.offers.weeklyPayment');
    if (paymentFrequency === 'BI-WEEKLY') return t('digitalLeads.vehicle.offers.biWeeklyPayment');
    return t('digitalLeads.vehicle.offers.monthlyPayment');
  }, [paymentFrequency, t]);

  const termForCPO = cpoTerm && cpoTerm.replace('apr', '').replace('_', ' - ');

  const offersNewCAAlpha = useMemo(() => {
    return (
      <OfferWrapper>
        <MainOfferData>
          <PercentData>
            <TextAlpha
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('digitalLeads.vehicle.offers.apr')}
            </TextAlpha>

            <Percent data-testid="offer-new-apr">
              {aprRate && (
                <HeadLineAlpha
                  tag="h4"
                  variant={{
                    order: '4',
                    type: 'headline',
                    weight: 'bold',
                  }}
                >
                  {`${(aprRate * 100).toFixed(2)}%`}
                </HeadLineAlpha>
              )}
            </Percent>
            <TextAlpha
              data-testid="offer-new-term"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('digitalLeads.vehicle.offers.upTo')} {term}{' '}
              {t('digitalLeads.vehicle.offers.months')}
            </TextAlpha>
          </PercentData>
          <DividerWrapper>
            <Divider orientation="vertical" />
          </DividerWrapper>
          <PaymentData>
            <TextAlpha
              data-testid="offer-new-freq"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {paymentFreq}
            </TextAlpha>
            <Percent>
              <HeadLineAlpha
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(Number((monthlyPayment ?? 0).toFixed()), lang)}
              </HeadLineAlpha>
            </Percent>
            <TermCa>
              <TextAlpha
                data-testid="offer-new-downpayment"
                variant={{
                  order: '2',
                  style: 'normal',
                }}
              >
                {t('digitalLeads.vehicle.offers.with')} {numberFormatter(customerDownPayment, lang)}{' '}
                {t('digitalLeads.vehicle.offers.downPayment')}
              </TextAlpha>
            </TermCa>
          </PaymentData>
        </MainOfferData>
        {!!creditBonus && (
          <BonusOffer>
            <div
              style={{
                color: 'hsla(216, 33%, 99%, 0.7)',
              }}
            >
              <IconAlpha size="s" name="special-offer" />
            </div>
            <TextAlpha margin={[0, 0, '8px', 0]} variant={{ order: '1', style: 'normal' }} tag="p">
              {numberFormatter(creditBonus, lang)} {t('digitalLeads.vehicle.offers.bonusOffer')}
            </TextAlpha>
          </BonusOffer>
        )}
      </OfferWrapper>
    );
  }, [aprRate, creditBonus, customerDownPayment, lang, monthlyPayment, paymentFreq, t, term]);

  const offersNewCA = useMemo(() => {
    return (
      <OfferWrapper>
        <MainOfferData>
          <PercentData>
            {process.env.IS_LEGACY ? (
              <Text
                variant={{
                  order: '2',
                  style: 'normal',
                }}
              >
                {t('digitalLeads.vehicle.offers.apr')}
              </Text>
            ) : (
              <TextAlpha
                variant={{
                  order: '2',
                  style: 'normal',
                }}
              >
                {t('digitalLeads.vehicle.offers.apr')}
              </TextAlpha>
            )}

            <Percent data-testid="offer-new-apr">
              {aprRate && (
                <Headline
                  tag="h4"
                  variant={{
                    order: '4',
                    type: 'headline',
                    weight: 'bold',
                  }}
                >
                  {`${(aprRate * 100).toFixed(2)}%`}
                </Headline>
              )}
            </Percent>
            <Text
              data-testid="offer-new-term"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('digitalLeads.vehicle.offers.upTo')} {term}{' '}
              {t('digitalLeads.vehicle.offers.months')}
            </Text>
          </PercentData>
          <DividerWrapper>
            <Divider orientation="vertical" />
          </DividerWrapper>
          <PaymentData>
            <Text
              data-testid="offer-new-freq"
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {paymentFreq}
            </Text>
            <Percent>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(Number((monthlyPayment ?? 0).toFixed()), lang)}
              </Headline>
            </Percent>
            <TermCa>
              <Text
                data-testid="offer-new-downpayment"
                variant={{
                  order: '2',
                  style: 'normal',
                }}
              >
                {t('digitalLeads.vehicle.offers.with')} {numberFormatter(customerDownPayment, lang)}{' '}
                {t('digitalLeads.vehicle.offers.downPayment')}
              </Text>
            </TermCa>
          </PaymentData>
        </MainOfferData>
        {!!creditBonus && (
          <BonusOffer>
            <Icon size="s" name="special-offer" />
            <Headline
              tag="h4"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'bold',
              }}
            >
              {numberFormatter(creditBonus, lang)} {t('digitalLeads.vehicle.offers.bonusOffer')}
            </Headline>
          </BonusOffer>
        )}
      </OfferWrapper>
    );
  }, [aprRate, creditBonus, customerDownPayment, lang, monthlyPayment, paymentFreq, t, term]);

  const offersCpoCAAlpha = useMemo(() => {
    return (
      <OfferWrapper>
        <PercentData>
          <TextAlpha
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {t('digitalLeads.vehicle.offers.apr')}
          </TextAlpha>
          <Percent data-testid="offer-new-apr">
            {aprRate && (
              <HeadLineAlpha
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {`${(aprRate * 100).toFixed(2)}%`}
              </HeadLineAlpha>
            )}
          </Percent>
          <TextAlpha
            data-testid="offer-new-term"
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {termForCPO} {t('digitalLeads.vehicle.offers.months')}
          </TextAlpha>
        </PercentData>
      </OfferWrapper>
    );
  }, [aprRate, t, termForCPO]);

  const offersCpoCA = useMemo(() => {
    return (
      <OfferWrapper>
        <PercentData>
          <Text
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {t('digitalLeads.vehicle.offers.apr')}
          </Text>
          <Percent data-testid="offer-new-apr">
            {aprRate && (
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {`${(aprRate * 100).toFixed(2)}%`}
              </Headline>
            )}
          </Percent>
          <Text
            data-testid="offer-new-term"
            variant={{
              order: '2',
              style: 'normal',
            }}
          >
            {termForCPO} {t('digitalLeads.vehicle.offers.months')}
          </Text>
        </PercentData>
      </OfferWrapper>
    );
  }, [aprRate, t, termForCPO]);

  const offersUSAlpha = useMemo(() => {
    return (
      <>
        <MainOffer>
          <AprOrPayment isFinanceOffer={isFinanceOffer}>
            <HeadLineAlpha
              margin={['0', '0', '0', '0']}
              variant={{ type: 'headline', order: '4', weight: 'bold' }}
              tag="h3"
            >
              {isFinanceOffer && offers.aprRate
                ? `${(offers.aprRate * 100).toFixed(2)}%`
                : numberFormatter(monthlyPayment, lang)}
            </HeadLineAlpha>
            <TextAlpha margin={[0, 0, 0, 0]} variant={{ order: '2', style: 'normal' }} tag="p">
              {isFinanceOffer
                ? t('digitalLeads.vehicle.offers.apr')
                : t('digitalLeads.vehicle.offers.monthlyPayment')}
            </TextAlpha>
          </AprOrPayment>
          <Term isFinanceOffer={isFinanceOffer}>
            <HeadLineAlpha
              margin={['0', '0', '0', '0']}
              variant={{ type: 'headline', order: '4', weight: 'bold' }}
              tag="h3"
            >
              {offers.term}
            </HeadLineAlpha>
            <TextAlpha margin={[0, 0, 0, 0]} variant={{ order: '2', style: 'normal' }} tag="p">
              {t('digitalLeads.vehicle.offers.months')}
            </TextAlpha>
          </Term>
          {!isFinanceOffer && (
            <DownPayment>
              <HeadLineAlpha
                margin={['0', '0', '0', '0']}
                variant={{ type: 'headline', order: '4', weight: 'bold' }}
                tag="h3"
              >
                {numberFormatter(offers.customerDownPayment, lang)}
              </HeadLineAlpha>
              <TextAlpha margin={[0, 0, 0, 0]} variant={{ order: '2', style: 'normal' }} tag="p">
                {t('digitalLeads.vehicle.offers.downPayment')}
              </TextAlpha>
            </DownPayment>
          )}
        </MainOffer>
        {!!offers.creditBonus && (
          <BonusOffer>
            <div
              style={{
                color: 'hsla(216, 33%, 99%, 0.7)',
              }}
            >
              <IconAlpha size="s" name="special-offer" />
            </div>
            <TextAlpha margin={[0, 0, '8px', 0]} variant={{ order: '1', style: 'normal' }} tag="p">
              {numberFormatter(offers.creditBonus, lang)}{' '}
              {t('digitalLeads.vehicle.offers.bonusOffer')}
            </TextAlpha>
          </BonusOffer>
        )}
      </>
    );
  }, [
    isFinanceOffer,
    lang,
    monthlyPayment,
    offers.aprRate,
    offers.creditBonus,
    offers.customerDownPayment,
    offers.term,
    t,
  ]);

  const offersUS = useMemo(() => {
    return (
      <>
        <MainOffer>
          <AprOrPayment isFinanceOffer={isFinanceOffer}>
            <PaymentNumber>
              {isFinanceOffer && offers.aprRate
                ? `${(offers.aprRate * 100).toFixed(2)}%`
                : numberFormatter(monthlyPayment, lang)}
            </PaymentNumber>
            <PaymentLabel>
              {isFinanceOffer
                ? t('digitalLeads.vehicle.offers.apr')
                : t('digitalLeads.vehicle.offers.monthlyPayment')}
            </PaymentLabel>
          </AprOrPayment>
          <Term isFinanceOffer={isFinanceOffer}>
            <PaymentNumber>{offers.term}</PaymentNumber>
            <PaymentLabel>{t('digitalLeads.vehicle.offers.months')}</PaymentLabel>
          </Term>
          {!isFinanceOffer && (
            <DownPayment>
              <PaymentNumber>{numberFormatter(offers.customerDownPayment, lang)}</PaymentNumber>
              <PaymentLabel>{t('digitalLeads.vehicle.offers.downPayment')}</PaymentLabel>
            </DownPayment>
          )}
        </MainOffer>
        {!!offers.creditBonus && (
          <BonusOffer>
            <OffersTag /> {numberFormatter(offers.creditBonus, lang)}{' '}
            {t('digitalLeads.vehicle.offers.bonusOffer')}
          </BonusOffer>
        )}
      </>
    );
  }, [
    isFinanceOffer,
    lang,
    monthlyPayment,
    offers.aprRate,
    offers.creditBonus,
    offers.customerDownPayment,
    offers.term,
    t,
  ]);

  const renderNewCA = process.env.IS_LEGACY ? offersNewCA : offersNewCAAlpha;
  const renderOffersCpoCA = process.env.IS_LEGACY ? offersCpoCA : offersCpoCAAlpha;
  const offersCA = type === 'NEW-CAR' ? renderNewCA : renderOffersCpoCA;

  const renderOffersUS = process.env.IS_LEGACY ? offersUS : offersUSAlpha;
  const RenderOffers = isUSMarket ? renderOffersUS : offersCA;

  return <OffersWrapper>{RenderOffers}</OffersWrapper>;
};

export default Offers;
